import React, { useState } from 'react';
import { 
  Box, Typography, FormGroup, FormControlLabel, Checkbox, 
  Button, Alert, Paper
} from '@mui/material';

interface WorksheetSelectionProps {
  sheets: string[];
  onSheetSelect: (selectedSheets: string[]) => void;
  onBack: () => void;
}

const WorksheetSelection: React.FC<WorksheetSelectionProps> = ({ 
  sheets, 
  onSheetSelect,
  onBack
}) => {
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleToggleSheet = (sheet: string) => {
    setSelectedSheets(prev => 
      prev.includes(sheet)
        ? prev.filter(s => s !== sheet)
        : [...prev, sheet]
    );
    setError(null);
  };

  const handleSelectAll = () => {
    setSelectedSheets([...sheets]);
    setError(null);
  };

  const handleClearAll = () => {
    setSelectedSheets([]);
  };

  const handleSubmit = () => {
    if (selectedSheets.length === 0) {
      setError('Please select at least one worksheet');
      return;
    }
    
    onSheetSelect(selectedSheets);
  };

  return (
    <Box>
      <Typography variant="h2" gutterBottom>
        Select Worksheets to Analyze
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body1">
            Found {sheets.length} worksheet{sheets.length !== 1 ? 's' : ''} in this file.
          </Typography>
          <Box>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleSelectAll}
              sx={{ mr: 1 }}
            >
              Select All
            </Button>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleClearAll}
            >
              Clear All
            </Button>
          </Box>
        </Box>

        <FormGroup>
          {sheets.map((sheet) => (
            <FormControlLabel
              key={sheet}
              control={
                <Checkbox
                  checked={selectedSheets.includes(sheet)}
                  onChange={() => handleToggleSheet(sheet)}
                />
              }
              label={sheet}
            />
          ))}
        </FormGroup>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onBack}>
          Back
        </Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleSubmit}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default WorksheetSelection;